import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import { getLocalizedText as getLocalizedTextFromTours } from './useTours'

// Re-export getLocalizedText for convenience
export { getLocalizedTextFromTours as getLocalizedText }

export interface InternationalizedText {
  en: string
  ar: string
  ur: string
  fa: string
}

export interface Hotel {
  id: string
  name: InternationalizedText
  city: InternationalizedText
  address: InternationalizedText
  latitude?: number | null
  longitude?: number | null
  images: string[]
  description: InternationalizedText
  phone?: string
  email?: string
  website?: string
  rating?: number | null
  deletedAt?: string | null
}

export function useHotels(cityFilter?: string) {
  const [hotels, setHotels] = useState<Hotel[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { isAdmin } = useAuth()

  const fetchHotels = async () => {
    try {
      setLoading(true)
      
      let query = supabase
        .from('hotels')
        .select('*')
        .order('name_en', { ascending: true })

      // For non-admin users, filter out deleted hotels at database level
      if (!isAdmin) {
        query = query.is('deleted_at', null)
      }

      // Filter by city if provided
      if (cityFilter) {
        query = query.or(`city_en.ilike.%${cityFilter}%,city_ar.ilike.%${cityFilter}%,city_ur.ilike.%${cityFilter}%,city_fa.ilike.%${cityFilter}%`)
      }

      const { data: supabaseData, error: supabaseError } = await query

      if (supabaseError) {
        throw new Error('Failed to fetch hotels data from Supabase')
      } else {
        const transformedHotels: Hotel[] = supabaseData.map((row) => ({
          id: row.id,
          name: {
            en: row.name_en || '',
            ar: row.name_ar || '',
            ur: row.name_ur || '',
            fa: row.name_fa || ''
          },
          city: {
            en: row.city_en || '',
            ar: row.city_ar || '',
            ur: row.city_ur || '',
            fa: row.city_fa || ''
          },
          address: {
            en: row.address_en || '',
            ar: row.address_ar || '',
            ur: row.address_ur || '',
            fa: row.address_fa || ''
          },
          latitude: row.latitude,
          longitude: row.longitude,
          images: row.images || [],
          description: {
            en: row.description_en || '',
            ar: row.description_ar || '',
            ur: row.description_ur || '',
            fa: row.description_fa || ''
          },
          phone: row.phone,
          email: row.email,
          website: row.website,
          rating: row.rating,
          deletedAt: row.deleted_at
        }))
        
        const filteredHotels = isAdmin 
          ? transformedHotels
          : transformedHotels.filter(hotel => !hotel.deletedAt)
        setHotels(filteredHotels)
      }
      
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
      console.error('Error fetching hotels:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchHotels()
  }, [cityFilter])

  // Get unique cities from hotels
  const getUniqueCities = (): string[] => {
    const cities = new Set<string>()
    hotels.forEach(hotel => {
      cities.add(hotel.city.en)
    })
    return Array.from(cities).sort()
  }

  return { hotels, loading, error, refetch: fetchHotels, getUniqueCities }
}

// Helper function to get hotel by city name
export function getHotelsByCity(hotels: Hotel[], cityName: string, language: string = 'en'): Hotel[] {
  return hotels.filter(hotel => {
    const hotelCity = getLocalizedTextFromTours(hotel.city, language).toLowerCase()
    return hotelCity === cityName.toLowerCase() || 
           hotel.city.en.toLowerCase() === cityName.toLowerCase()
  })
}

